import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Check if user must change password - with long staleTime to avoid repeated fetches
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['profile-must-change-password', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('must_change_password')
        .eq('user_id', user.id)
        .single();
      if (error) {
        console.error('Error fetching profile:', error);
        return null;
      }
      return data;
    },
    enabled: !!user,
    // Cache for 30 minutes - this rarely changes
    staleTime: 1000 * 60 * 30,
    // Keep in cache for 1 hour
    gcTime: 1000 * 60 * 60,
    // Don't refetch on window focus
    refetchOnWindowFocus: false,
    // Don't refetch on mount if we have data
    refetchOnMount: false,
  });

  // Show loading only if we're actually loading (not cached)
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Show loading for profile only on first load
  if (profileLoading && !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Redirect to change-password if needed (but not if already on that page)
  if (profile?.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  return <>{children}</>;
}
